import { useState, useEffect } from 'react';
import { mockOffices } from '../../services/mockData';
import { authService } from '../../services/authService';

const CompensationPage = () => {
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOffice, setEditingOffice] = useState(null);
  const currentUser = authService.getCurrentUser();
  const isBrokerAdmin = currentUser?.role === 'BrokerAdmin';

  useEffect(() => {
    setTimeout(() => {
      setOffices(mockOffices.filter(o => o.brokerId === 1));
      setLoading(false);
    }, 500);
  }, []);

  const handleEdit = (office) => {
    setEditingOffice({ ...office });
  };

  const handleSave = () => {
    setOffices(offices.map(o => 
      o.id === editingOffice.id ? editingOffice : o
    ));
    setEditingOffice(null);
  };

  const handleCancel = () => {
    setEditingOffice(null);
  };

  const handleChange = (field, value) => {
    setEditingOffice({
      ...editingOffice,
      compensation: {
        ...editingOffice.compensation,
        [field]: parseFloat(value) || 0
      }
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-header d-print-none">
      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title">Compensation Management</h2>
          <div className="text-secondary mt-1">Configure compensation structure by office</div>
        </div>
      </div>

      {!isBrokerAdmin && (
        <div className="alert alert-warning mb-4">
          <div className="d-flex">
            <div>
              <i className="ti ti-lock me-2"></i>
            </div>
            <div>
              <h4 className="alert-title">Read-Only Access</h4>
              <div className="text-secondary">
                You are viewing compensation information in read-only mode. Only Broker Admins can edit compensation settings.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="alert alert-info mb-4">
        <div className="d-flex">
          <div>
            <i className="ti ti-info-circle me-2"></i>
          </div>
          <div>
            <h4 className="alert-title">Compensation Rules</h4>
            <div className="text-secondary">
              Compensation is calculated as: <strong>(Loan Amount × Percentage) + Flat Fee</strong>
              <br />
              The result is capped between the minimum and maximum compensation values.
              <br />
              <strong>These settings apply to all users in the respective office.</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="row row-cards">
        {offices.map((office) => (
          <div key={office.id} className="col-12">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  {office.name}
                  {office.isMain && (
                    <span className="badge bg-blue-lt ms-2">Main Office</span>
                  )}
                </h3>
                <div className="card-actions">
                  {editingOffice?.id === office.id ? (
                    <>
                      <button className="btn btn-sm btn-success me-2" onClick={handleSave}>
                        <i className="ti ti-check me-1"></i>
                        Save
                      </button>
                      <button className="btn btn-sm btn-ghost-secondary" onClick={handleCancel}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button 
                      className="btn btn-sm btn-primary" 
                      onClick={() => handleEdit(office)}
                      disabled={!isBrokerAdmin}
                    >
                      <i className="ti ti-edit me-1"></i>
                      Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <label className="form-label">Percentage of Loan Amount</label>
                    {editingOffice?.id === office.id ? (
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          step="0.1"
                          value={editingOffice.compensation.percentage}
                          onChange={(e) => handleChange('percentage', e.target.value)}
                        />
                        <span className="input-group-text">%</span>
                      </div>
                    ) : (
                      <div className="h2 mb-0 text-primary">{office.compensation.percentage}%</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Flat Dollar Fee</label>
                    {editingOffice?.id === office.id ? (
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          value={editingOffice.compensation.flatFee}
                          onChange={(e) => handleChange('flatFee', e.target.value)}
                        />
                      </div>
                    ) : (
                      <div className="h2 mb-0 text-primary">${office.compensation.flatFee.toLocaleString()}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Minimum Compensation</label>
                    {editingOffice?.id === office.id ? (
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          value={editingOffice.compensation.minComp}
                          onChange={(e) => handleChange('minComp', e.target.value)}
                        />
                      </div>
                    ) : (
                      <div className="h3 mb-0">${office.compensation.minComp.toLocaleString()}</div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Maximum Compensation</label>
                    {editingOffice?.id === office.id ? (
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number"
                          className="form-control"
                          value={editingOffice.compensation.maxComp}
                          onChange={(e) => handleChange('maxComp', e.target.value)}
                        />
                      </div>
                    ) : (
                      <div className="h3 mb-0">${office.compensation.maxComp.toLocaleString()}</div>
                    )}
                  </div>
                </div>

                <hr className="my-3" />

                <div className="row">
                  <div className="col-12">
                    <h4 className="card-subtitle mb-3">Example Calculations</h4>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Loan Amount</th>
                            <th>Calculation</th>
                            <th>Final Compensation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[300000, 500000, 750000].map(amount => {
                            const calc = (amount * (office.compensation.percentage / 100)) + office.compensation.flatFee;
                            const final = Math.min(Math.max(calc, office.compensation.minComp), office.compensation.maxComp);
                            return (
                              <tr key={amount}>
                                <td>${amount.toLocaleString()}</td>
                                <td className="text-secondary">
                                  (${amount.toLocaleString()} × {office.compensation.percentage}%) + ${office.compensation.flatFee.toLocaleString()} = ${calc.toLocaleString()}
                                </td>
                                <td className="fw-bold text-success">${final.toLocaleString()}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompensationPage;
